import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { anchorFor, placeAnchor } from '@tmea/shared';
import {
  api,
  type ClauseDetail,
  type Passage,
  type PlacedAnnotation,
  type Verb,
} from '../lib/api.js';
import { Annotatable, type Selection } from './Annotatable.js';
import { ActionBar, Composer, MarkList, VERBS, type AnchoredSelection } from './Marks.js';
import { marginLabel } from './margin.js';
import { DraftingDefects, ExtractionArtefacts } from './Defects.js';
import { isSettled } from './settled.js';
import { readNotesOnPage, writeNotesOnPage } from './notesPref.js';

export type Register = 'Legal' | 'Plain' | 'Split';

/**
 * The clause reader.
 *
 * Typographic rendering of the parsed source, never a PDF viewer. A node with
 * no drafted text opens onto a briefing rather than a dead end — an outline
 * click that shows nothing teaches people the tool is empty, and that is the
 * failure this product cannot afford early on.
 */
/**
 * The clause text with its marks, the selection bar, and the composer.
 *
 * Anchors are resolved here rather than trusted from the database. Stored
 * offsets are a claim about a version of the text; `placeAnchor` checks that
 * claim against what is actually on screen and reports a mark whose words have
 * gone as orphaned rather than drawing it over whatever now sits at those
 * offsets. A highlight that silently slides onto a different provision is
 * worse than no highlight at all.
 */
function MarkedText({
  clause,
  focus,
  notesOnPage,
  onSelect,
  onAsk,
  onCompare,
}: {
  clause: ClauseDetail;
  focus: string | null;
  /** Whether the post-its are stuck to the passages or only in the list. */
  notesOnPage: boolean;
  onSelect: (number: string) => void;
  onAsk: (seed: string) => void;
  onCompare: (number: string) => void;
}) {
  const client = useQueryClient();

  const [composing, setComposing] = useState<{
    verb: Verb;
    clause: string;
    selection: AnchoredSelection;
  } | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  /**
   * The passages this page renders.
   *
   * Each stays a separate annotatable region rather than being concatenated
   * into one string. Concatenating would read the same and would quietly
   * break every anchor, because a mark's offsets are into its own clause's
   * text — 11.5(b) starts at 0, not at wherever it happens to fall on screen.
   */
  const passages = useMemo(
    () => clause.descendants.filter((p) => p.textVerbatim.trim().length > 0 || p.heading),
    [clause.descendants],
  );

  // Marks are fetched for the provision, not the opened node. The annotations
  // route returns a subtree, so asking for 11.5 excludes its siblings and a
  // highlight just made on 11.8 would vanish on save.
  const scopeRoot = clause.provision?.number ?? passages[0]?.number ?? clause.number;

  const marks = useQuery({
    queryKey: ['annotations', scopeRoot],
    queryFn: () => api.annotations(scopeRoot),
  });

  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['annotations', scopeRoot] });
    void client.invalidateQueries({ queryKey: ['unpublished'] });
  }, [client, scopeRoot]);

  // Placement is a substring search per mark, and this component re-renders
  // on every hover (activeId lives here) — so anchors resolve once per
  // (marks, passages) pair, not twice per mark per render.
  const placedByPassage = useMemo(() => {
    const map = new Map<string, PlacedAnnotation[]>();
    for (const passage of passages) {
      map.set(
        passage.number,
        (marks.data?.annotations ?? [])
          .filter((mark) => mark.clauseNumber === passage.number)
          .map((mark) => {
            const where = placeAnchor(passage.textVerbatim, {
              exact: mark.exact,
              prefix: mark.prefix ?? '',
              suffix: mark.suffix ?? '',
              start: mark.start,
              end: mark.end,
            });
            return where
              ? { ...mark, start: where.start, end: where.end, orphaned: false }
              : // Kept so it can still be read and answered, but given no
                // position, so `segment` will not draw it over other words.
                { ...mark, start: -1, end: -1, orphaned: true };
          }),
      );
    }
    return map;
  }, [marks.data, passages]);

  const placedFor = (number: string): PlacedAnnotation[] => placedByPassage.get(number) ?? [];
  const everyMark = [...placedByPassage.values()].flat();

  // The provision is the page, so opening 11.5 and opening 11 land on the
  // same page. Highlighting the node actually asked for is what makes them
  // read as "your clause, in its provision" rather than as one page shown
  // twice.
  const highlight =
    focus ?? (passages.length > 0 && passages[0]!.number !== clause.number ? clause.number : null);

  return (
    <>
      {/*
        Above the text, not below it. Someone about to read a damaged clause
        needs to know before they read it, not after they have formed a view
        from something the agreement does not say.
      */}
      <ExtractionArtefacts artefacts={clause.artefacts ?? []} formulas={clause.formulas ?? []} />

      <ProvisionContents passages={passages} opened={clause.number} onSelect={onSelect} />

      {passages.map((passage) => (
        <Passage_
          key={passage.number}
          passage={passage}
          opened={clause.number}
          focused={passage.number === highlight}
          annotations={placedFor(passage.number)}
          activeId={activeId}
          notesOnPage={notesOnPage}
          onOpenMark={setActiveId}
          onHighlight={(selection) => {
            void saveHighlight(passage.number, passage.textVerbatim, selection).then(refresh);
          }}
          onCompose={(verb, selection) =>
            setComposing({
              verb,
              clause: passage.number,
              selection: withContext(passage.textVerbatim, selection),
            })
          }
        />
      ))}

      {composing ? (
        <Composer
          clause={composing.clause}
          verb={composing.verb}
          selection={composing.selection}
          onAsk={onAsk}
          onCompare={onCompare}
          onDone={() => {
            setComposing(null);
            window.getSelection()?.removeAllRanges();
            refresh();
          }}
          onCancel={() => setComposing(null)}
        />
      ) : null}

      <MarkList
        // The scope root, not the opened node: that is the key the marks
        // query lives under, and MarkList's own edits invalidate it.
        clause={scopeRoot}
        annotations={everyMark}
        activeId={activeId}
        onActivate={setActiveId}
      />
    </>
  );
}

/**
 * The subclauses of a long provision, as a strip across the top.
 *
 * A provision is the whole numbered clause, which is right — it is how the
 * agreement is printed and how it is argued about — but clause 24 runs to
 * fifteen thousand characters across sixty-eight nodes, and a page that long
 * with no way in is a page people scroll past. legislation.gov.uk solves the
 * same problem the same way, with anchors into a long section.
 *
 * Only for provisions that need it. Below five numbered subclauses the strip
 * is longer than the reading it saves, and a clause of three paragraphs is
 * already one glance.
 */
function ProvisionContents({
  passages,
  opened,
  onSelect,
}: {
  passages: readonly Passage[];
  opened: string;
  onSelect: (number: string) => void;
}) {
  // Numbered subclauses only. Lettered paragraphs are the texture of a clause
  // rather than places anyone navigates to.
  const entries = passages.filter(
    (p) => p.kind === 'subclause' || (p.kind === 'clause' && p.number !== opened),
  );
  if (entries.length < 5) return null;

  return (
    <nav
      className="mb-5 border-b border-[var(--color-rule)] pb-3"
      aria-label="In this clause"
    >
      <p className="label mb-1.5">In this clause</p>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map((entry) => (
          <li key={entry.number}>
            <button
              onClick={() => onSelect(entry.number)}
              className="hit text-left text-[12px] text-[var(--color-muted)] underline underline-offset-2"
            >
              <span className="clause-number mr-1 text-[11px]">{entry.number}</span>
              {entry.heading ?? entry.textVerbatim.slice(0, 40)}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * One passage: a clause, a subclause, or a lettered paragraph.
 *
 * The number sits in the margin rather than inline, so the prose column keeps
 * a straight left edge and the numbering reads as the index it is. A passage
 * that carries a heading gets one, because in a wider scope the headings are
 * what make a long read navigable.
 */
function Passage_({
  passage,
  opened,
  focused,
  annotations,
  activeId,
  notesOnPage,
  onOpenMark,
  onHighlight,
  onCompose,
}: {
  passage: Passage;
  opened: string;
  focused: boolean;
  annotations: readonly PlacedAnnotation[];
  activeId: string | null;
  notesOnPage: boolean;
  onOpenMark: (id: string) => void;
  onHighlight: (selection: Selection) => void;
  onCompose: (verb: Verb, selection: Selection) => void;
}) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const host = useRef<HTMLDivElement>(null);

  // A citation naming a paragraph opens the clause around it, so the paragraph
  // has to say which one it was.
  useEffect(() => {
    if (focused) host.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);

  const label = marginLabel(passage.number, opened);
  const isParagraph = passage.kind === 'paragraph';
  const hasText = passage.textVerbatim.trim().length > 0;

  /*
   * A subclause heading is a section title and sits at the column's left edge
   * with its full number. A paragraph's heading is different: 11.8(a)
   * "Full-Time and Part-Time Teachers" is a sub-heading *inside* 11.8, and
   * rendering it full-number at the same level as 11.8 read as the paragraph
   * escaping its clause. It keeps its margin label and its indent, exactly
   * like the text beneath it.
   *
   * The opened clause keeps its heading too, which it used to lose.
   *
   * The reasoning for dropping it was that the page title above already says
   * "11.1 Notice of Termination is provided for in the NES", so repeating it
   * five lines down is duplication. That holds for exactly as long as both are
   * on screen. Open 11.1 and the reader shows the whole of clause 11 with the
   * selected passage boxed inside it — so every sibling carries its number and
   * heading and the one you actually came for carries neither, and as soon as
   * the register controls and the in-this-clause index have scrolled past, the
   * boxed passage is the only unlabelled thing on the page.
   *
   * Duplicating a heading costs a line. Not having it costs you the answer to
   * "which clause am I looking at", in a document where that question is the
   * whole point.
   */
  const isSectionHeading = Boolean(passage.heading) && !isParagraph;

  return (
    <div
      ref={host}
      id={`c-${passage.number}`}
      className="mt-3"
      style={
        focused
          ? { background: 'var(--color-wash)', outline: '1px solid var(--color-rule-strong)' }
          : undefined
      }
    >
      {isSectionHeading ? (
        /* 600, the same weight as the paragraph headings one level below it.
           At 500 a subclause heading was lighter than the (a) heading nested
           inside it, so the hierarchy read upside down; size carries the
           difference instead. */
        <h2
          className="register-reading mb-1 mt-5 flex items-baseline gap-2"
          style={{ fontSize: 15, fontWeight: 600 }}
        >
          <span className="clause-number text-[11px]" style={{ color: 'var(--color-muted)' }}>
            {passage.number}
          </span>
          {passage.heading}
        </h2>
      ) : null}

      {hasText || (isParagraph && passage.heading) ? (
        <div className="flex gap-3">
          {label && !isSectionHeading ? (
            /* Flush right against the prose, the way the printed agreement
               sets it and the way Ask sets a citation's number: (a), (b), (c)
               and 11.5.1 are different widths, so a left-aligned gutter puts
               the number a different distance from every line it labels. */
            <span
              className="clause-number shrink-0 pt-1 text-right text-[11px] text-[var(--color-muted)]"
              style={{ minWidth: 40 }}
            >
              {label}
            </span>
          ) : null}

          <div className="min-w-0 flex-1">
            {isParagraph && passage.heading ? (
              <p className="register-reading mb-0.5" style={{ fontSize: 14, fontWeight: 600 }}>
                {passage.heading}
              </p>
            ) : null}
            <Annotatable
              text={passage.textVerbatim}
              annotations={annotations}
              activeId={activeId}
              onSelect={(next, position) => {
                setSelection(next);
                setAt(position);
              }}
              onOpenMark={onOpenMark}
            >
              {selection && at ? (
                <ActionBar
                  at={at}
                  onVerb={(verb) => {
                    // Highlight is the one verb that needs no words, so it is
                    // the one gesture that never opens a composer.
                    if (verb === 'highlight') onHighlight(selection);
                    else onCompose(verb, selection);
                    setSelection(null);
                    if (verb === 'highlight') window.getSelection()?.removeAllRanges();
                  }}
                />
              ) : null}
            </Annotatable>

            {notesOnPage ? (
              <Postits marks={annotations} activeId={activeId} onOpenMark={onOpenMark} />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The notes on this passage, stuck to it.
 *
 * A note existed only as a card in the list under the clause, and on clause 24
 * — sixty-eight nodes, fifteen thousand characters — that list is several
 * screens below the sentence it is about. By the time you reach "this is the
 * pro-rata trap" you have long since read past the trap.
 *
 * Below the passage rather than beside it. A margin column looks better and
 * does not survive the arithmetic: at 1280px the canvas is roughly 650px wide
 * once the rail and the dock have taken theirs, and a 200px note column would
 * take the reading measure down to about 390px — trading the thing this
 * product is for against a note position.
 *
 * The list below is unchanged and is still the record: it carries publishing,
 * replies and settling. This is the pointer, where the words are.
 */
function Postits({
  marks,
  activeId,
  onOpenMark,
}: {
  marks: readonly PlacedAnnotation[];
  activeId: string | null;
  onOpenMark: (id: string) => void;
}) {
  // A highlight has no words in it, and an orphan's words are no longer on
  // this passage — sticking either to the text would be a post-it with
  // nothing written on it, or one stuck to the wrong paragraph.
  const notes = marks.filter((mark) => mark.body?.trim() && !mark.orphaned);
  if (notes.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Notes on this passage"
      className="mt-2 flex flex-wrap items-start gap-2"
    >
      {notes.map((mark) => {
        const hue = mark.authorColour ? `var(--color-${mark.authorColour})` : 'var(--color-muted)';
        const entry = VERBS.find((v) => v.verb === mark.verb);
        return (
          <button
            key={mark.id}
            type="button"
            // Click, not hover. `onOpenMark` only ever sets an id — there is
            // no path back to null — so lighting a passage on hover would
            // leave the last one hovered lit for the rest of the session.
            onClick={() => onOpenMark(mark.id)}
            className={`note note-postit text-left ${mark.id === activeId ? 'note-active' : ''} ${
              mark.visibility === 'private' ? 'note-private' : ''
            } ${isSettled(mark) ? 'note-settled' : ''}`}
            style={{ '--note-hue': hue } as CSSProperties}
          >
            <span className="label flex items-baseline gap-1.5">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: hue }}
              />
              {mark.isAnonymous ? 'Anonymous' : (mark.authorName ?? 'Someone')}
              <span aria-hidden>{entry?.glyph}</span>
              {isSettled(mark) ? <span style={{ color: 'var(--color-win)' }}>✓</span> : null}
            </span>
            <span className="register-reading mt-1 block text-[13px]">{mark.body}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Adds the W3C context selectors a bare screen selection does not carry. */
function withContext(text: string, selection: Selection): AnchoredSelection {
  const anchor = anchorFor(text, selection.start, selection.end);
  return { ...selection, prefix: anchor?.prefix ?? '', suffix: anchor?.suffix ?? '' };
}

async function saveHighlight(clause: string, text: string, selection: Selection): Promise<void> {
  const full = withContext(text, selection);
  await api.annotate({
    clause,
    verb: 'highlight',
    exact: full.exact,
    prefix: full.prefix,
    suffix: full.suffix,
    start: full.start,
    end: full.end,
    isAnonymous: false,
    visibility: 'private',
  });
}

export function Reader({
  clause,
  focus,
  register,
  onRegister,
  onSelect,
  onAsk,
  onCompare,
}: {
  clause: ClauseDetail;
  /** A paragraph within this clause that a citation named, to scroll to. */
  focus: string | null;
  register: Register;
  onRegister: (register: Register) => void;
  onSelect: (number: string) => void;
  /** Carries a passage into Ask, seeded as the question. */
  onAsk: (seed: string) => void;
  /** Opens Compare on this clause. */
  onCompare: (number: string) => void;
}) {
  const defects = clause.refs.outbound.filter((r) => r.status !== 'resolved');

  // Read once, from the browser's store. A preference that resets on reload is
  // a preference nobody sets twice.
  const [notesOnPage, setNotesOnPage] = useState(readNotesOnPage);

  // Whether the *reading block* has text, not whether this one node does.
  //
  // This was the bug. Once title-only clauses had their headings restored,
  // 11.5 and 10.2 held no text of their own — all of it is in 11.5(a) and (b)
  // — so `clause.textVerbatim` was empty and the reader fell through to the
  // outline briefing. The result read "This is a container: its terms are in
  // the clauses beneath it", listing the paragraphs by number with no words
  // at all. The agreement was there the whole time and the reader refused to
  // show it.
  const hasText = clause.descendants.some((p) => p.textVerbatim.trim().length > 0);
  const passageCount = clause.descendants.filter(
    (p) => p.textVerbatim.trim().length > 0,
  ).length;

  return (
    /*
      `.screen` for the 28px inset, `overflow-visible` to give back the one
      thing the primitive assumes.

      The primitive bundles `overflow-y: auto` with the padding, which is right
      for a surface that owns its own scrolling. This one does not: the shell's
      <main> already scrolls, and a second scroll container here would compute
      overflow-x to auto as well and clip the selection ActionBar — which is
      absolutely positioned at `top - 44`, i.e. deliberately outside the
      passage it belongs to.
    */
    <article className="screen overflow-visible">
      {/* The header sits above a section boundary, which is the stronger of
          the three rule weights — the clause number and its heading are not
          another card in the stack, they are what the stack is about. */}
      <div className="mb-5 flex items-baseline gap-3 border-b border-[var(--color-rule-strong)] pb-3">
        <span
          className="clause-number shrink-0"
          style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em' }}
        >
          {clause.number}
        </span>
        <h1
          className="register-reading m-0"
          style={{ fontSize: 23, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.01em' }}
        >
          {clause.heading ?? ''}
        </h1>
        {/* What is actually on screen, the way the printed agreement would
            describe it: how much text, and how many findings to expect. */}
        <span className="label ml-auto shrink-0 whitespace-nowrap">
          {passageCount > 0 ? `${passageCount} passage${passageCount === 1 ? '' : 's'}` : ''}
          {defects.length > 0 ? ` · ${defects.length} defective ref` : ''}
          {clause.pageStart ? ` · p${clause.pageStart}` : ''}
        </span>
      </div>

      {hasText ? (
        <>
          {/* The Clause / Section / Part control is gone. It existed because
              the node was the wrong unit — clause 10.2 has no text of its own
              and 11.5 is a title — and asked the reader to solve that by
              guessing a width. The provision is the unit now, so there is
              nothing to choose. */}
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
            {(['Legal', 'Plain', 'Split'] as const).map((option) => {
              const on = option === register;
              const available = option === 'Legal' || clause.textPlain;
              return (
                /*
                  A chip in the mono micro-label register, not a sans button.
                  The register control names a way of reading the document, so
                  it belongs to the same connective tissue as every other panel
                  name — and at 10px sans it was the one control on the page
                  that looked like it came from a different product.

                  `hit` because 10px text at py-0.5 measured about 16px tall,
                  under the 24px WCAG 2.2 minimum the rest of the app was
                  already fixed to.
                */
                <button
                  key={option}
                  disabled={!available}
                  onClick={() => onRegister(option)}
                  aria-pressed={on}
                  className="hit label rounded-[4px] border px-2 py-1"
                  style={{
                    borderColor: on ? 'var(--color-ink)' : 'var(--color-rule)',
                    background: on ? 'var(--color-ink)' : 'transparent',
                    color: on
                      ? 'var(--color-paper)'
                      : available
                        ? 'var(--color-muted)'
                        : 'var(--color-rule-strong)',
                  }}
                >
                  {option}
                </button>
              );
            })}
            </div>

            {/*
              The post-its, on or off.

              Next to the register control because it is the same kind of
              decision — how this page should read — and because a long clause
              with fifteen notes stuck to it is a page somebody will want to
              read clean, once, without deleting anything.
            */}
            <button
              onClick={() => {
                setNotesOnPage((on) => {
                  writeNotesOnPage(!on);
                  return !on;
                });
              }}
              aria-pressed={notesOnPage}
              className="hit label underline underline-offset-2"
              style={{ color: notesOnPage ? 'var(--color-ink)' : 'var(--color-muted)' }}
              title={
                notesOnPage
                  ? 'Notes are stuck to the passages they are about'
                  : 'Notes are in the list under the clause only'
              }
            >
              {/* ● is the annotation glyph in the design language's table. A
                  pictorial memo emoji stood here — the last one on this
                  surface — and the rule is outline glyphs only, because a
                  pictorial icon renders as a different picture on every
                  platform and prints as a blob. */}
              <span aria-hidden>●</span> Notes on the page
            </button>
          </div>

          {register !== 'Plain' ? (
            <MarkedText
              clause={clause}
              focus={focus}
              notesOnPage={notesOnPage}
              onSelect={onSelect}
              onAsk={onAsk}
              onCompare={onCompare}
            />
          ) : null}

          {register !== 'Legal' && clause.textPlain ? (
            /*
              A card, with its provenance in the header rather than in a line
              underneath it.

              Dashed rather than solid, and that is the one deviation from the
              plain `.card`: every other panel on this screen quotes the
              agreement, and this one does not. The mockup draws the same
              distinction the same way. The stamp moves up beside the panel
              name because a reader who scrolls past the heading has, by the
              second paragraph, no way left of knowing a machine wrote it.
            */
            <div
              className="card measure mt-4"
              style={{ borderStyle: 'dashed', borderColor: 'var(--color-rule-strong)' }}
            >
              <div className="card-header">
                <span className="label">In plain English</span>
                <span className="stamp stamp-ai">
                  AI drafted ·{' '}
                  {clause.plainState === 'unverified' ? 'unverified' : clause.plainState}
                </span>
              </div>
              <div className="register-reading" style={{ fontSize: 15.5 }}>
                {clause.textPlain}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <OutlineBriefing clause={clause} onSelect={onSelect} />
      )}

      {defects.length > 0 ? (
        /* One card holding the whole finding, not one card per broken
           reference. Clause 24 carries five, and five stacked panels read as
           five separate problems with the agreement rather than as the list of
           references this clause makes and cannot keep. */
        <section className="card measure mt-6">
          <div className="card-header">
            <span className="label">△ Drafting defects</span>
            <span className="label">{defects.length}</span>
          </div>
          {defects.map((ref) => (
            <div key={ref.toNumber} className="row flex-col items-start gap-1">
              <div className="clause-number text-[11px]" style={{ color: 'var(--color-defect)' }}>
                points at {ref.toNumber} — {ref.status.replace('_', ' ')}
              </div>
              {ref.suggestedFix ? (
                <div className="text-[12px] text-[var(--color-muted)]">{ref.suggestedFix}</div>
              ) : null}
              {ref.context ? (
                <div className="register-reading text-[13px] leading-relaxed">
                  “…{ref.context}…”
                </div>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      <DraftingDefects defects={clause.drafting ?? []} />

      {clause.leverage ? <Leverage leverage={clause.leverage} /> : null}
    </article>
  );
}

/**
 * What the workbench knows about a clause it has no drafted text for.
 *
 * This was a specific fix in the design: a briefing, not an apology.
 */
function OutlineBriefing({
  clause,
  onSelect,
}: {
  clause: ClauseDetail;
  onSelect: (number: string) => void;
}) {
  return (
    <div className="measure">
      {/* Serif at reading size, because it is the only prose on the page. A
          briefing rendered at 12px sans read as an error message, which is
          precisely what the briefing exists not to be. */}
      <p className="register-reading text-[15px] text-[var(--color-muted)]">
        {clause.isContainer
          ? 'This is a container: its terms are in the clauses beneath it.'
          : 'No drafted text for this node.'}
      </p>

      {clause.children.length > 0 ? (
        <section className="card mt-5">
          <div className="card-header">
            <span className="label">What it contains</span>
            <span className="label">{clause.children.length}</span>
          </div>
          <ul>
            {clause.children.map((child) => (
              <li key={child.number} className="border-b border-[var(--color-rule)] last:border-b-0">
                <button
                  onClick={() => onSelect(child.number)}
                  className="hit flex w-full items-baseline gap-2.5 py-[9px] text-left"
                >
                  {/* The number in its own gutter, as Ask sets a citation:
                      the eye finds the reference without reading the row. */}
                  <span className="clause-number w-14 shrink-0 text-right text-[11px]">
                    {child.number}
                  </span>
                  <span className="min-w-0 flex-1 text-[12.5px] leading-snug">
                    {child.heading ?? '—'}
                  </span>
                  {!child.hasText ? (
                    <span className="label shrink-0">outline</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {clause.claims.length > 0 ? (
        <section className="card mt-4">
          <div className="card-header">
            <span className="label">Claims relying on this clause</span>
          </div>
          {clause.claims.map((claim) => (
            <div key={claim.ref} className="row">
              <span className="clause-number w-14 shrink-0 text-right text-[11px]">
                {claim.ref}
              </span>
              <span className="min-w-0 flex-1 text-[12.5px] leading-snug">{claim.title}</span>
              <span className="label shrink-0">{claim.status}</span>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}

function Leverage({ leverage }: { leverage: NonNullable<ClauseDetail['leverage']> }) {
  const rows: [string, string | null][] = [
    ['The ask', leverage.ask],
    ['The precedent', leverage.precedent],
    ['The pressure', leverage.pressure],
    ['What to watch', leverage.watch],
  ];
  return (
    /*
      The one inversion the design language allows, taking the card's geometry
      rather than its own.

      Every label on this ground is overridden to the stronger rule grey:
      `.label` resolves to --color-muted, which is #6e6e6b on #2a2a28 — 2.8:1,
      well under the 4.5:1 a 9px label needs. #b9b9b6 is 7.3:1.
    */
    <section
      className="card measure mt-6"
      style={{
        background: 'var(--color-ink)',
        borderColor: 'var(--color-ink)',
        color: 'var(--color-paper)',
      }}
    >
      <div className="card-header" style={{ borderColor: 'var(--color-muted)' }}>
        <span className="label" style={{ color: 'var(--color-rule-strong)' }}>
          Leverage
        </span>
        <span className="stamp" style={{ color: 'var(--color-rule-strong)' }}>
          {leverage.verifiedAt
            ? '✓ verified'
            : leverage.draftedBy === 'ai'
              ? '◔ AI drafted · unverified'
              : 'drafted by the group'}
        </span>
      </div>
      {rows.map(([label, value]) =>
        value ? (
          <div key={label} className="mb-3 last:mb-0">
            <div className="label mb-1" style={{ color: 'var(--color-rule-strong)' }}>
              {label}
            </div>
            <div className="register-reading text-[14px] leading-relaxed">{value}</div>
          </div>
        ) : null,
      )}
    </section>
  );
}
